function withHoursAliased(row) {
  if (!row || typeof row !== "object") return row;
  const o = { ...row };
  const k = "Hours Used (optional)";
  if (o[k] !== undefined && (o["Hours Used"] == null || o["Hours Used"] === "")) o["Hours Used"] = o[k];
  delete o[k];
  return o;
}

export const mapStationaryBulkHoursRows = (rows) =>
  Array.isArray(rows) ? rows.map(withHoursAliased) : rows;
/** Default quantity unit for bulk Excel when the parser mis-maps columns (matches template). */
export const NATURAL_GAS_DEFAULT_QUANTITY_UNIT = "M3";

/** Units used for quantity consumed on Natural Gas bulk rows. */
export const NATURAL_GAS_QUANTITY_UNIT_SET = new Set([
  "M3",
  "Liters",
  "Gallon",
  "Tonnes",
]);

/** Density is required for these quantity units (same as BulkUpload prop). */
export const NATURAL_GAS_DENSITY_REQUIRED_UNITS = [
  "M3",
  "Liters",
  "Gallon",
];

/** Bulk Excel column header for density (Tonnes/M³); used by Propane & LPG templates. */
export const PROPANE_BULK_DENSITY_COLUMN = "Density (Tonnes/M3)";

function hasDensityValue(out) {
  const d =
    out.Density ?? out.density ?? out[PROPANE_BULK_DENSITY_COLUMN];
  if (d === undefined || d === null || d === "") return false;
  return !Number.isNaN(Number(d));
}

function assignDensityIfMissing(out, value) {
  const d = Number(value);
  if (!Number.isFinite(d)) return;
  if (hasDensityValue(out)) return;
  out.density = d;
  out.Density = d;
}

/**
 * Backend bulk parse often maps Excel columns by fixed positions. When new columns
 * (Unit, Density) were added, values can land in `quantityConsumedUnit` ("10") or
 * `Unit of Quantity Consumed` (density). Repair so API enum `quantityConsumed.unit`
 * is valid and density is preserved. Invalid / numeric unit → default **M3** (Excel default).
 */
export function normalizeNaturalGasBulkRow(row) {
  if (!row || typeof row !== "object") return row;
  const out = withHoursAliased(row);

  let qcUnit = String(out.quantityConsumedUnit ?? "").trim();
  const unitCell = String(out["Unit of Quantity Consumed"] ?? "").trim();

  // Invalid enum on quantityConsumedUnit (e.g. "10") → default M3; numeric → density if missing
  if (!NATURAL_GAS_QUANTITY_UNIT_SET.has(qcUnit)) {
    if (
      qcUnit !== "" &&
      !Number.isNaN(Number(qcUnit)) &&
      Number.isFinite(Number(qcUnit))
    ) {
      assignDensityIfMissing(out, qcUnit);
    }
    out.quantityConsumedUnit = NATURAL_GAS_DEFAULT_QUANTITY_UNIT;
    qcUnit = NATURAL_GAS_DEFAULT_QUANTITY_UNIT;
  }

  // Valid unit + Excel "Unit" column is a number (density shifted into unit column)
  if (
    NATURAL_GAS_QUANTITY_UNIT_SET.has(qcUnit) &&
    unitCell &&
    unitCell !== qcUnit &&
    !Number.isNaN(Number(unitCell)) &&
    Number.isFinite(Number(unitCell))
  ) {
    assignDensityIfMissing(out, unitCell);
    out["Unit of Quantity Consumed"] = qcUnit;
  } else {
    out["Unit of Quantity Consumed"] = qcUnit;
  }

  // Final guard: never send invalid enum to API
  if (
    !NATURAL_GAS_QUANTITY_UNIT_SET.has(
      String(out.quantityConsumedUnit ?? "").trim()
    )
  ) {
    out.quantityConsumedUnit = NATURAL_GAS_DEFAULT_QUANTITY_UNIT;
  }

  const dens = out.Density ?? out.density;
  if (dens !== undefined && dens !== null && dens !== "") {
    const n = Number(dens);
    if (Number.isFinite(n)) {
      out.density = n;
      out.Density = n;
    }
  }

  // Propane bulk template uses labelled density column — mirror to Density/density for API
  const altD = out[PROPANE_BULK_DENSITY_COLUMN];
  if (altD !== undefined && altD !== null && altD !== "") {
    const n = Number(altD);
    if (Number.isFinite(n)) {
      out.density = n;
      out.Density = n;
    }
  }

  // Keep nested shape in sync when backend / client sends `quantityConsumed` object
  const uFinal = String(out.quantityConsumedUnit ?? "").trim();
  const safeUnit = NATURAL_GAS_QUANTITY_UNIT_SET.has(uFinal)
    ? uFinal
    : NATURAL_GAS_DEFAULT_QUANTITY_UNIT;
  if (out.quantityConsumed && typeof out.quantityConsumed === "object") {
    const qv =
      out.quantityConsumed?.value ?? out["Quantity Consumed (in M3)"];
    out.quantityConsumed = {
      ...out.quantityConsumed,
      unit: safeUnit,
      ...(qv !== undefined && qv !== "" && Number.isFinite(Number(qv))
        ? { value: Number(qv) }
        : {}),
    };
  } else if (
    out["Quantity Consumed (in M3)"] != null &&
    String(out["Quantity Consumed (in M3)"]).trim() !== ""
  ) {
    const qv = Number(out["Quantity Consumed (in M3)"]);
    if (Number.isFinite(qv)) {
      out.quantityConsumed = { value: qv, unit: safeUnit };
    }
  }

  return out;
}

export function mapNaturalGasBulkRows(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map(normalizeNaturalGasBulkRow);
}
